import { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const DL = { muay_thai_educatif: "Kickboxing/Muaythai", kickboxing_educatif: "Kickboxing/Muaythai", jjb: "JJB", can_ryu_demo: "Can-Ryu", petits_samourais: "Petits Samouraïs" };
const STATUS_FR = {
  draft: "Brouillon", submitted: "Soumise", docs_missing: "Documents manquants", validated: "Validée", checked_in: "Check-in",
  ready: "Prête", in_progress: "En cours", done: "Terminée", forfeit: "Forfait", disqualified: "Disqualifiée",
};
const STATUS_COLOR = {
  draft: "bg-zinc-800 text-zinc-300", submitted: "bg-sky-900/50 text-sky-200", docs_missing: "bg-amber-900/50 text-amber-200",
  validated: "bg-emerald-900/50 text-emerald-200", checked_in: "bg-emerald-800/60 text-emerald-100", ready: "bg-lime-900/50 text-lime-200",
  in_progress: "bg-purple-900/50 text-purple-200", done: "bg-zinc-700 text-zinc-100", forfeit: "bg-red-900/50 text-red-200", disqualified: "bg-red-800/70 text-red-100",
};

// Sprint 1 — Participations par discipline (1 ligne = 1 athlète × 1 discipline).
export function ParticipationsTab({ eventId }) {
  const [data, setData] = useState(null);
  const [disc, setDisc] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    api.get(`/jm/events/${eventId}/entries${disc ? `?discipline=${disc}` : ""}`).then((r) => setData(r.data)).catch(() => toast.error("Erreur chargement participations"));
  }, [eventId, disc]);
  useEffect(() => { load(); }, [load]);

  const patch = async (id, body) => {
    setBusyId(id);
    try { await api.patch(`/jm/entries/${id}`, body); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setBusyId(null);
  };
  const setStatus = (row, status) => {
    if (status === "forfeit" || status === "disqualified") {
      const note = window.prompt(`Motif obligatoire (décision d'un officiel) — ${STATUS_FR[status]} pour ${row.athlete_name} :`);
      if (!note) return;
      return patch(row.id, { status, organizer_note: note });
    }
    return patch(row.id, { status });
  };
  const decide = async (row, decision) => {
    const note = decision === "refused" ? window.prompt(`Motif du refus — ${row.athlete_name} · ${DL[row.discipline] || row.discipline} :`) : (window.prompt("Note (optionnelle) :") || "");
    if (decision === "refused" && !note) return;
    try { await api.post(`/jm/entries/${row.id}/approval`, { decision, note }); toast.success(decision === "approved" ? "Participation approuvée" : "Participation refusée"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const editField = (row, field, label, type = "text") => {
    const v = window.prompt(`${label} — ${row.athlete_name} :`, row[field] ?? "");
    if (v === null) return;
    patch(row.id, { [field]: type === "number" ? parseFloat(v) : v });
  };

  if (!data) return <div className="text-zinc-500 text-sm">Chargement…</div>;
  const { summary, entries, event } = data;
  return (
    <div className="space-y-3" data-testid="tab-content-participations">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="px-2 py-1 rounded bg-zinc-900 border border-zinc-800" data-testid="part-total">{summary.total} participation{summary.total > 1 ? "s" : ""}</span>
        {Object.entries(summary.by_discipline).map(([d, n]) => (
          <button key={d} onClick={() => setDisc(disc === d ? "" : d)} data-testid={`part-filter-${d}`}
            className={`px-2 py-1 rounded border ${disc === d ? "border-amber-400 text-amber-300" : "border-zinc-800 text-zinc-400 hover:text-zinc-200"}`}>{DL[d] || d} · {n}</button>
        ))}
        {summary.pending_approval > 0 && <span className="px-2 py-1 rounded bg-amber-900/40 border border-amber-700 text-amber-200" data-testid="part-pending">⚠️ {summary.pending_approval} à approuver (≥ {event.manual_approval_from}ᵉ discipline)</span>}
        <span className="ml-auto text-zinc-500">Max {event.max_disciplines} disciplines · approbation manuelle dès la {event.manual_approval_from}ᵉ{event.is_test ? " · 🧪 BAC À SABLE" : ""}</span>
      </div>
      {entries.length === 0 ? <p className="text-zinc-500 text-sm">Aucune participation. Les inscriptions existantes sont converties automatiquement (1 ligne par discipline).</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="participations-table">
            <thead className="text-[10px] uppercase tracking-widest text-zinc-500">
              <tr><th className="text-left py-1">Athlète</th><th className="text-left">Discipline</th><th>Ceinture</th><th>Poids</th><th>Catégorie</th><th>Statut</th><th>Plateau</th><th>Approbation</th></tr>
            </thead>
            <tbody>
              {entries.map((r) => (
                <tr key={r.id} className={`border-t border-zinc-800 ${busyId === r.id ? "opacity-50" : ""}`} data-testid={`part-row-${r.id}`}>
                  <td className="py-1.5"><div className="font-medium text-zinc-100">{r.athlete_name}</div><div className="text-zinc-500">{r.school_name} · {r.athlete_age} ans · #{r.discipline_index}</div></td>
                  <td className="text-zinc-200">{DL[r.discipline] || r.discipline}</td>
                  <td className="text-center"><button onClick={() => editField(r, "belt_grade", "Ceinture / grade")} className="underline decoration-dotted text-zinc-300">{r.belt_grade || "—"}</button></td>
                  <td className="text-center"><button onClick={() => editField(r, "declared_weight_kg", "Poids déclaré (kg)", "number")} className="underline decoration-dotted text-zinc-300">{r.declared_weight_kg ?? "—"}{r.official_weight_kg ? ` / ${r.official_weight_kg} off.` : ""}</button></td>
                  <td className="text-center"><button onClick={() => editField(r, "provisional_category", "Catégorie provisoire")} className="underline decoration-dotted text-zinc-300" data-testid={`part-cat-${r.id}`}>{r.final_category || r.provisional_category || "—"}</button></td>
                  <td className="text-center">
                    <select value={r.status} onChange={(e) => setStatus(r, e.target.value)} data-testid={`part-status-${r.id}`}
                      className={`rounded px-1.5 py-0.5 text-[11px] border border-zinc-700 ${STATUS_COLOR[r.status]}`}>
                      {data.statuses.map((s) => <option key={s} value={s} className="bg-zinc-900 text-zinc-100">{STATUS_FR[s]}</option>)}
                    </select>
                  </td>
                  <td className="text-center"><button onClick={() => editField(r, "plateau", "Plateau / aire")} className="underline decoration-dotted text-zinc-300">{r.plateau || "—"}</button></td>
                  <td className="text-center">
                    {!r.needs_approval ? <span className="text-zinc-600">auto</span>
                      : r.organizer_approval ? <span className={r.organizer_approval.decision === "approved" ? "text-emerald-300" : "text-red-300"} title={r.organizer_approval.note}>{r.organizer_approval.decision === "approved" ? "✅ approuvée" : "⛔ refusée"}</span>
                      : <span className="flex gap-1 justify-center"><button onClick={() => decide(r, "approved")} data-testid={`part-approve-${r.id}`} className="px-2 py-0.5 rounded bg-emerald-700 hover:bg-emerald-600 text-white">✓</button><button onClick={() => decide(r, "refused")} data-testid={`part-refuse-${r.id}`} className="px-2 py-0.5 rounded bg-red-800 hover:bg-red-700 text-white">✕</button></span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
